import { AlertCircle, Clock, MapPin, User, Wrench } from 'lucide-react';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { cn } from '../../lib/utils';
import type { Job, Skill } from '../../types/job-plan';
import { Badge } from '../../ui/badge';

export interface JobCardProps {
  job: Job;
  onClick?: () => void;
  operatorName?: string | null;
  plannedArrivalSeconds?: number | null;
  travelGapMinutes?: number | null;
  windowViolation?: boolean;
  className?: string;
}

export const JOB_STATUS_COLOR_MAP: Record<string, string> = {
  draft: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
  unassigned: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  assigned: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20',
  en_route: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  in_progress: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  canceled: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
  failed: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

export function JobCard({
  job,
  onClick,
  operatorName,
  plannedArrivalSeconds,
  travelGapMinutes,
  windowViolation,
  className,
}: JobCardProps) {
  const statusClass = JOB_STATUS_COLOR_MAP[job.status] || JOB_STATUS_COLOR_MAP.unassigned;

  const hasWindow = job.windowStartOffsetSeconds != null && job.windowEndOffsetSeconds != null;
  const windowStr = hasWindow
    ? `${formatOffsetTime(job.windowStartOffsetSeconds!)} - ${formatOffsetTime(
        job.windowEndOffsetSeconds!
      )}`
    : null;

  const durationMin = Math.round(job.estimatedDurationSeconds / 60);

  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl border border-border/50 bg-card p-3.5 shadow-sm transition-all hover:shadow-md cursor-pointer flex flex-col gap-2.5',
        job.status === 'completed' && 'opacity-70',
        windowViolation && 'border-red-500/40 bg-red-500/[0.02]',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-sm line-clamp-1 text-card-foreground">{job.title}</span>
        <Badge variant="outline" className={cn('text-[11px] capitalize shrink-0', statusClass)}>
          {job.status.replace('_', ' ')}
        </Badge>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
        <span className="truncate">{job.formattedAddress}</span>
      </div>

      {job.contact && (
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/90">
          <User className="h-3 w-3 shrink-0 text-primary/70" />
          <span className="truncate font-medium text-foreground/80">
            {job.contact.name}
            {job.contact.company ? ` (${job.contact.company})` : ''}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {windowStr && (
          <div
            className={cn('flex items-center gap-1 font-medium', windowViolation && 'text-red-500')}
          >
            {windowViolation ? (
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-500" />
            ) : (
              <Clock className="h-3.5 w-3.5 shrink-0" />
            )}
            <span>{windowStr}</span>
          </div>
        )}

        {plannedArrivalSeconds != null && (
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-muted-foreground/80">Plan:</span>
            <span className="font-medium text-card-foreground">
              {formatOffsetTime(plannedArrivalSeconds)}
            </span>
          </div>
        )}

        <div className="text-[11px] text-muted-foreground/80">{durationMin}m service</div>

        {travelGapMinutes != null && (
          <div className="text-[11px] text-muted-foreground/70">+{travelGapMinutes}m drive</div>
        )}
      </div>

      {job.requiredSkills && job.requiredSkills.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 mt-0.5">
          <Wrench className="h-3 w-3 text-muted-foreground/60 shrink-0" />
          {job.requiredSkills.map((skill: Skill) => (
            <span
              key={skill.id}
              style={{
                backgroundColor: skill.color ? `${skill.color}20` : undefined,
                color: skill.color || undefined,
                borderColor: skill.color ? `${skill.color}40` : undefined,
              }}
              className="px-1.5 py-0.5 text-[10px] font-medium rounded border border-border/40 bg-secondary/50"
            >
              {skill.name}
            </span>
          ))}
        </div>
      )}

      {operatorName && (
        <div className="flex items-center gap-1.5 pt-1 border-t border-border/40 text-xs text-muted-foreground">
          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
          <span className="truncate font-medium">{operatorName}</span>
        </div>
      )}
    </div>
  );
}
